/**
 * Snapshot Builder
 *
 * Builds payment snapshots from extraction context using registered fields.
 *
 * ## Data Source Priority
 *
 * When building bond breakdowns, prefers `bondAllocations` (calculated at capture time)
 * over `cashFlow.bondPayments` (pre-distribution values). The fallback exists for
 * post-distribution captures or legacy code paths.
 */

import { DebugFieldRegistry } from '../field-registry';
import type {
  BondPaymentSnapshot,
  CapturedFieldValue,
  DebugCaptureConfig,
  DebugExtractionContext,
  PaymentSnapshot,
} from '../types';

/**
 * Build a payment snapshot from extraction context
 */
export function buildPaymentSnapshot(
  context: DebugExtractionContext,
  config: DebugCaptureConfig
): PaymentSnapshot {
  const registry = DebugFieldRegistry.getInstance();
  const enabledFields = registry.getEnabledFields(config);

  // Extract field values
  const fields: Record<string, CapturedFieldValue> = {};

  for (const fieldDef of enabledFields) {
    try {
      const value = fieldDef.extractor(context);

      // Skip undefined values
      if (value === undefined) continue;

      // Format the value
      const formatted = fieldDef.formatter ? fieldDef.formatter(value) : String(value);

      // Get formula if enabled
      const formula =
        config.captureFormulas && fieldDef.formula ? fieldDef.formula(context) : undefined;

      fields[fieldDef.id] = {
        value,
        formatted,
        formula,
        fieldId: fieldDef.id,
        fieldName: fieldDef.name,
      };
    } catch (error) {
      // Log extraction error but continue
      console.warn(`[DebugCapture] Error extracting field '${fieldDef.id}':`, error);
    }
  }

  // Build bond breakdown if enabled
  let bondBreakdown: BondPaymentSnapshot[] | undefined;
  if (config.captureBondBreakdown && (context.bondAllocations || context.cashFlow?.bondPayments)) {
    bondBreakdown = buildBondBreakdown(context);
  }

  return {
    paymentDate: context.cashFlow?.paymentDate ?? '',
    taxYear: context.cashFlow?.relatedTaxBillYear ?? 0,
    paymentIndex: context.paymentIndex,
    isInterestOnly: context.cashFlow?.isInterestOnly ?? false,
    isActive: context.cashFlow?.isActive ?? false,
    fields,
    bondBreakdown,
  };
}

/**
 * Build per-bond breakdown for a payment
 * Prefers bondAllocations (calculated values) over cashFlow.bondPayments (pre-distribution values)
 */
function buildBondBreakdown(context: DebugExtractionContext): BondPaymentSnapshot[] {
  const { bondAllocations, cashFlow, bondSeries } = context;

  // Prefer bondAllocations (has accurate calculated values)
  if (bondAllocations && bondAllocations.length > 0) {
    // Build a lookup for bond series metadata
    const bondSeriesMap = new Map(bondSeries.map((bs) => [bs.id, bs]));

    return bondAllocations.map((alloc) => {
      const fields: Record<string, CapturedFieldValue> = {};
      const seriesInfo = bondSeriesMap.get(alloc.bondSeriesId);

      // Core bond payment fields from allocation
      fields['bond.principal'] = {
        value: alloc.principal,
        formatted: `$${alloc.principal.toLocaleString()}`,
        fieldId: 'bond.principal',
        fieldName: 'Principal',
      };

      fields['bond.interestRate'] = {
        value: alloc.interestRate,
        formatted: `${(alloc.interestRate * 100).toFixed(2)}%`,
        fieldId: 'bond.interestRate',
        fieldName: 'Interest Rate',
      };

      fields['bond.newInterest'] = {
        value: alloc.newInterest,
        formatted: `$${alloc.newInterest.toLocaleString()}`,
        fieldId: 'bond.newInterest',
        fieldName: 'New Interest',
      };

      fields['bond.futureInterestObligation'] = {
        value: alloc.futureInterestObligation,
        formatted: `$${alloc.futureInterestObligation.toLocaleString()}`,
        fieldId: 'bond.futureInterestObligation',
        fieldName: 'Future Interest Obligation',
      };

      fields['bond.totalInterest'] = {
        value: alloc.totalInterest,
        formatted: `$${alloc.totalInterest.toLocaleString()}`,
        fieldId: 'bond.totalInterest',
        fieldName: 'Total Interest',
      };

      fields['bond.totalPayment'] = {
        value: alloc.totalPayment,
        formatted: `$${alloc.totalPayment.toLocaleString()}`,
        fieldId: 'bond.totalPayment',
        fieldName: 'Total Payment',
      };

      return {
        bondSeriesId: alloc.bondSeriesId,
        bondSeriesName: seriesInfo?.name ?? alloc.bondSeriesId,
        // If an allocation exists, the bond is active for this payment
        isActive: true,
        fields,
      };
    });
  }

  // Fallback to cashFlow.bondPayments (legacy path)
  if (!cashFlow?.bondPayments) return [];

  return cashFlow.bondPayments.map((bp) => {
    const fields: Record<string, CapturedFieldValue> = {};

    // Core bond payment fields
    fields['bond.principal'] = {
      value: bp.principal,
      formatted: `$${bp.principal.toLocaleString()}`,
      fieldId: 'bond.principal',
      fieldName: 'Principal',
    };

    fields['bond.interestRate'] = {
      value: bp.interestRate,
      formatted: `${(bp.interestRate * 100).toFixed(2)}%`,
      fieldId: 'bond.interestRate',
      fieldName: 'Interest Rate',
    };

    fields['bond.newInterest'] = {
      value: bp.newInterest,
      formatted: `$${bp.newInterest.toLocaleString()}`,
      fieldId: 'bond.newInterest',
      fieldName: 'New Interest',
    };

    fields['bond.futureInterestObligation'] = {
      value: bp.futureInterestObligation,
      formatted: `$${bp.futureInterestObligation.toLocaleString()}`,
      fieldId: 'bond.futureInterestObligation',
      fieldName: 'Future Interest Obligation',
    };

    fields['bond.totalInterest'] = {
      value: bp.totalInterest,
      formatted: `$${bp.totalInterest.toLocaleString()}`,
      fieldId: 'bond.totalInterest',
      fieldName: 'Total Interest',
    };

    fields['bond.totalPayment'] = {
      value: bp.totalPayment,
      formatted: `$${bp.totalPayment.toLocaleString()}`,
      fieldId: 'bond.totalPayment',
      fieldName: 'Total Payment',
    };

    return {
      bondSeriesId: bp.bondSeriesId,
      bondSeriesName: bp.bondSeriesName,
      isActive: bp.isActive,
      fields,
    };
  });
}
